import { inject, Injectable } from '@angular/core';
import { concatMap, forkJoin, from, mergeMap, Observable, of, throwError } from 'rxjs';
import { catchError, finalize, map, shareReplay, switchMap, take, toArray } from 'rxjs/operators';
import { FileCacheModel, IFileCache } from '../models';
import { FileDownloaderService } from './file-downloader.service';
import { FileCacheRepository } from '../repositories/file-cache.repository';

export interface FileCacheMetadata {
  size?: number | null;
  mimeType?: string | null;
}

export type FileCacheDownloadInput = string | ({
  url: string;
} & FileCacheMetadata);

@Injectable()
export class FileCacheService {

  private readonly downloader = inject(FileDownloaderService);
  private readonly filesCacheRepository = inject(FileCacheRepository);
  private readonly inFlightDownloads = new Map<string, Observable<Blob>>();

  public downloadFile(url: string, metadata: FileCacheMetadata = {}): Observable<IFileCache> {
    return this.filesCacheRepository.one$(url).pipe(
      take(1),
      switchMap((file) => {
        if (this.isValidFile(file, metadata)) {
          return of(new FileCacheModel(file));
        }

        return this.downloadAndCache(url, metadata);
      }),
    );
  }

  public downloadFiles(files: FileCacheDownloadInput[]): Observable<IFileCache[]> {
    if (files.length === 0) {
      return of([]);
    }

    const entries = files.map((file) => this.normalizeFileInput(file));

    return forkJoin(entries.map(({ url, metadata }) => this.downloadFile(url, metadata)));
  }

  public add(fileUrl: string, blob: Blob): Observable<IFileCache> {
    return this.upsert(fileUrl, blob);
  }

  private upsert(fileUrl: string, blob: Blob): Observable<IFileCache> {
    const value: IFileCache = {
      url: fileUrl,
      blob,
      createdAt: Date.now()
    };

    return this.filesCacheRepository.one$(fileUrl).pipe(
      take(1),
      switchMap((file) => {
        if (file) {
          return this.filesCacheRepository.update$(value);
        }

        return this.filesCacheRepository.add$(value).pipe(
          catchError(() => this.filesCacheRepository.update$(value)),
        );
      }),
    );
  }

  public delete(url: string): Observable<boolean> {
    return this.filesCacheRepository.delete$(url).pipe(
      map(() => true),
    );
  }

  public bulkDelete(urls: string[]): Observable<boolean[]> {
    if (urls.length === 0) {
      return of([]);
    }

    return from(urls).pipe(
      concatMap((url) => this.filesCacheRepository.delete$(url)),
      toArray(),
    );
  }

  private downloadAndCache(url: string, metadata: FileCacheMetadata): Observable<IFileCache> {
    return this.getInFlightDownload(url).pipe(
      mergeMap((blob) => {
        if (!this.isValidBlob(blob, metadata)) {
          return throwError(() => new Error(`Downloaded file is invalid: ${ url }`));
        }

        return this.upsert(url, blob);
      }),
      map((file) => new FileCacheModel(file)),
    );
  }

  private getInFlightDownload(url: string): Observable<Blob> {
    const inFlightDownload = this.inFlightDownloads.get(url);

    if (inFlightDownload) {
      return inFlightDownload;
    }

    const download$ = this.downloader.downloadAsBlob(url).pipe(
      finalize(() => {
        this.inFlightDownloads.delete(url);
      }),
      shareReplay({ bufferSize: 1, refCount: false }),
    );

    this.inFlightDownloads.set(url, download$);

    return download$;
  }

  private normalizeFileInput(file: FileCacheDownloadInput): { url: string; metadata: FileCacheMetadata } {
    if (typeof file === 'string') {
      return {
        url: file,
        metadata: {},
      };
    }

    return {
      url: file.url,
      metadata: {
        size: file.size,
        mimeType: file.mimeType,
      },
    };
  }

  private isValidFile(file: IFileCache | undefined, metadata: FileCacheMetadata): file is IFileCache {
    return !!file && this.isValidBlob(file.blob, metadata);
  }

  private isValidBlob(blob: Blob | undefined, metadata: FileCacheMetadata): blob is Blob {
    if (!(
      blob instanceof Blob
    )) {
      return false;
    }

    if (blob.size <= 0) {
      return false;
    }

    const actualMimeType = this.normalizeMimeType(blob.type);

    if (this.isErrorMimeType(actualMimeType)) {
      return false;
    }

    if (typeof metadata.size === 'number' && blob.size !== metadata.size) {
      return false;
    }

    const expectedMimeType = this.normalizeMimeType(metadata.mimeType);

    if (expectedMimeType && (
      !actualMimeType || !this.isMimeTypeCompatible(actualMimeType, expectedMimeType)
    )) {
      return false;
    }

    return true;
  }

  private normalizeMimeType(mimeType: string | null | undefined): string {
    return mimeType?.split(';')[0].trim().toLowerCase() ?? '';
  }

  private isErrorMimeType(mimeType: string): boolean {
    return mimeType === 'text/html'
      || mimeType === 'application/json'
      || mimeType === 'text/plain';
  }

  private isMimeTypeCompatible(actualMimeType: string, expectedMimeType: string): boolean {
    const normalizedExpectedMimeType = this.normalizeMimeType(expectedMimeType);

    if (!normalizedExpectedMimeType) {
      return true;
    }

    if (actualMimeType === normalizedExpectedMimeType) {
      return true;
    }

    if (normalizedExpectedMimeType.endsWith('/*')) {
      return actualMimeType.startsWith(normalizedExpectedMimeType.slice(0, -1));
    }

    if (!normalizedExpectedMimeType.includes('/')) {
      return actualMimeType.startsWith(`${ normalizedExpectedMimeType }/`);
    }

    if (normalizedExpectedMimeType.endsWith('/')) {
      return actualMimeType.startsWith(normalizedExpectedMimeType);
    }

    return false;
  }

}
